import type {
	CreateEquipmentMutationRequest,
	UpdateEquipmentMutationRequest,
} from "@packages/api-client/types/equipmentController";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCreateEquipment } from "@/api/equipment";
import { PageHeader } from "@/components/PageHeader";
import { requireReachableRoute } from "@/lib/offline/routeGuard";
import { EquipmentForm } from "./-components/EquipmentForm";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/equipment/fleet/new",
)({
	// Registering an asset is administrative desk work — online-only by design.
	staticData: { offline: "online-only" },
	beforeLoad: () => requireReachableRoute("online-only"),
	component: NewEquipmentPage,
});

function NewEquipmentPage() {
	const { orgId } = Route.useParams();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const createMutation = useCreateEquipment();

	const handleSubmit = (
		formData: CreateEquipmentMutationRequest | UpdateEquipmentMutationRequest,
	) =>
		createMutation
			.mutateAsync({
				organizationId: orgId,
				data: formData as CreateEquipmentMutationRequest,
			})
			.then(() => {
				navigate({
					to: "/app/org/$orgId/equipment/fleet",
					params: { orgId },
				});
			});

	return (
		<div className="flex flex-col gap-6 h-full">
			<PageHeader title={t("equipment.fleet.create.title")} showBackButton />
			<EquipmentForm
				orgId={orgId}
				onSubmit={handleSubmit}
				isSubmitting={createMutation.isPending}
				onCancel={() =>
					navigate({
						to: "/app/org/$orgId/equipment/fleet",
						params: { orgId },
					})
				}
			/>
		</div>
	);
}
